import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Users,
  Target,
  BarChart3,
  Shield,
  CheckCircle,
  Globe,
  Heart,
  TrendingUp
} from 'lucide-react';

const ForNGOs = () => {
  const programIntegration = [
    {
      title: "Vaccination Programs",
      description: "Coordinate with national vaccination campaigns and ensure coverage for AMHAS children",
      icon: Shield
    },
    {
      title: "Deworming Initiatives",
      description: "Integrate deworming programs with school-based health checkups",
      icon: Heart
    },
    {
      title: "WASH Programs",
      description: "Water, sanitation, and hygiene education aligned with health promotion",
      icon: Globe
    },
    {
      title: "Nutrition Support",
      description: "Nutritional assessments and supplementation programs for malnourished children",
      icon: Target
    }
  ];

  const targetingAreas = [
    "Rural and remote communities with limited healthcare access",
    "Low-income urban areas with high child health needs",
    "Schools in underserved regions",
    "Communities affected by health disparities",
    "Areas with high child mortality rates",
    "Regions with limited healthcare infrastructure"
  ];

  const dataSharing = [
    {
      title: "Health Outcomes",
      description: "Aggregated data on child health improvements and health trends"
    },
    {
      title: "Program Effectiveness",
      description: "Metrics on attendance, academic performance, and family savings"
    },
    {
      title: "Geographic Coverage",
      description: "Maps and data on service coverage and access patterns"
    },
    {
      title: "Impact Assessment",
      description: "Regular reports on program impact and community health improvements"
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="/urgentcare_payments_community.png" 
            alt="AMHAS Healthcare" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">For NGOs & Government</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Provider with AMHAS to expand healthcare access and improve child health outcomes through school-based programs and telehealth integration.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Program Integration */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Program Integration</h2>
            <p className="section-subtitle">
              Align your health programs with AMHAS to maximize impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {programIntegration.map((program, index) => {
              const Icon = program.icon;
              return (
                <motion.div
                  key={program.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8"
                >
                  <div className="flex items-start space-x-4">
                    <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon size={32} className="text-primary-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{program.title}</h3>
                      <p className="text-gray-600">{program.description}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Targeting & Equity */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Targeting & Equity</h2>
            <p className="section-subtitle">
              Focus on rural and low-income cohorts for maximum impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Priority Areas</h3>
              <ul className="space-y-4">
                {targetingAreas.map((area, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle size={20} className="text-accent-600 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">{area}</span>
                  </li>
                ))}
              </ul>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Equity Focus</h3>
              <div className="space-y-6">
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Sliding Scale Pricing</h4>
                  <p className="text-gray-600 text-sm">Reduced premiums for low-income families to ensure accessibility.</p>
                </div>
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Donor Subsidies</h4>
                  <p className="text-gray-600 text-sm">Financial support for families who cannot afford premiums.</p>
                </div>
                <div className="card p-6">
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">Rural Outreach</h4>
                  <p className="text-gray-600 text-sm">Special programs and pricing for remote communities.</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Data & Reporting */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Data & Reporting</h2>
            <p className="section-subtitle">
              Transparent reporting with strict data protection safeguards
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {dataSharing.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card p-8"
              >
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <BarChart3 size={24} className="text-accent-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.description}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
            className="mt-12 text-center"
          >
            <div className="card p-8 max-w-4xl mx-auto">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Data Protection & Safeguards</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <Shield size={32} className="text-primary-600 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900">Privacy First</div>
                  <div className="text-gray-600 text-sm">Strict data protection protocols</div>
                </div>
                <div className="text-center">
                  <Users size={32} className="text-accent-600 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900">Anonymized Data</div>
                  <div className="text-gray-600 text-sm">No personal information shared</div>
                </div>
                <div className="text-center">
                  <CheckCircle size={32} className="text-green-600 mx-auto mb-2" />
                  <div className="text-lg font-semibold text-gray-900">Regular Audits</div>
                  <div className="text-gray-600 text-sm">Compliance monitoring and reviews</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Working with AMHAS */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Benefits of Working with AMHAS</h2>
            <p className="section-subtitle">
              How providering with AMHAS amplifies your impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: TrendingUp,
                title: "Scale Your Impact",
                description: "Reach more children through our established network of schools and clinics"
              },
              {
                icon: Target,
                title: "Strategic Alignment",
                description: "Align with national health strategies and development goals"
              },
              {
                icon: BarChart3,
                title: "Data-Driven Insights",
                description: "Access comprehensive data and reporting for program evaluation"
              }
            ].map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8 text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={32} className="text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Provider With Us</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Join us in creating a healthier future for children across Cameroon & The Gambia.
              Contact us to discuss enrollment opportunities and pilot programs.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <Link to="/contact" className="btn-secondary">
                Contact for MOUs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ForNGOs;
